import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, UserPlus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import AddEditStudentDialog from "@/components/alunos/AddEditStudentDialog";
import AlunosTable from "@/components/alunos/AlunosTable";
import { TurmaStatsCards } from "@/components/alunos/TurmaStatsCards";
import { ImportarNotasDialog } from "@/components/notas/ImportarNotasDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useEscolaConfig } from "@/contexts/EscolaConfigContext";
import {
  useAlunosTurma,
  type AlunoTurmaResumo,
} from "@/hooks/useAlunosTurma";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const GerenciarAlunosPage = () => {
  const { turmaId } = useParams<{ turmaId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { config } = useEscolaConfig();
  const corPrimaria = config?.cor_primaria || "#6D28D9";
  const isManager = [
    "admin",
    "diretor",
    "coordenador",
    "secretario",
    "super_admin",
  ].includes(user?.role || "");

  const [alunoParaRemover, setAlunoParaRemover] = useState<{
    id: string;
    nome: string;
  } | null>(null);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);
  const [selectedAluno, setSelectedAluno] = useState<AlunoTurmaResumo | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const {
    alunos,
    setAlunos,
    turmaInfo,
    loading,
    refreshAlunos,
  } = useAlunosTurma(turmaId, [
    "id",
    "nome",
    "matricula",
    "turma_id",
    "nome_responsavel",
    "telefone_responsavel",
    "data_nascimento",
  ]);

  const handleEditar = (id: string) => {
    const aluno = alunos.find(item => item.id === id);
    if (!aluno) return;

    setSelectedAluno(aluno);
    setIsEditing(true);
    setShowAddEditDialog(true);
  };

  const handleAddAluno = () => {
    setSelectedAluno(null);
    setIsEditing(false);
    setShowAddEditDialog(true);
  };

  const confirmarRemocao = async () => {
    if (!alunoParaRemover || isRemoving) return;

    setIsRemoving(true);
    try {
      const { error: presencasError } = await supabase
        .from("presencas")
        .delete()
        .eq("aluno_id", alunoParaRemover.id);

      if (presencasError) throw presencasError;

      const { error: alunoError } = await supabase
        .from("alunos")
        .delete()
        .eq("id", alunoParaRemover.id);

      if (alunoError) throw alunoError;

      setAlunos(current => current.filter(aluno => aluno.id !== alunoParaRemover.id));
      toast({
        title: "Aluno removido",
        description: `O aluno ${alunoParaRemover.nome} foi removido com sucesso.`,
      });
      setAlunoParaRemover(null);
    } catch (error) {
      console.error("Erro ao remover aluno:", error);
      toast({
        title: "Não foi possível remover o aluno",
        description: "Verifique vínculos pendentes e tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleStudentAdded = () => {
    setShowAddEditDialog(false);
    refreshAlunos();
  };

  return (
    <div className="min-h-0">
      <div className="app-page max-w-7xl">
        <div className="app-page-header sm:items-center">
          <Button
            variant="ghost"
            className="h-11 w-11 shrink-0"
            onClick={() => navigate("/dashboard")}
            aria-label="Voltar para o painel"
          >
            <ArrowLeft size={22} />
          </Button>
          <div className="min-w-0 pt-1 sm:pt-0">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Turmas / Alunos
            </p>
            <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">
              Gerenciar alunos
              {turmaInfo && (
                <span className="block sm:inline" style={{ color: corPrimaria }}>
                  {" "}· {turmaInfo.nome}
                </span>
              )}
            </h1>
          </div>
        </div>

        {turmaInfo && <TurmaStatsCards turmaInfo={turmaInfo} />}

        <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 sm:text-xl">
                Lista de alunos
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Frequência calculada com base nos últimos 30 dias.
              </p>
            </div>

            {isManager && (
              <div className="flex w-full gap-2 sm:w-auto">
                <ImportarNotasDialog />
                <Button
                  className="h-11 flex-1 justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 sm:flex-none"
                  onClick={handleAddAluno}
                >
                  <UserPlus size={20} />
                  Adicionar aluno
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              Carregando alunos...
            </div>
          ) : alunos.length > 0 ? (
            <AlunosTable
              alunos={alunos}
              onRemove={aluno => setAlunoParaRemover(aluno)}
              onEdit={handleEditar}
              canEdit={isManager}
            />
          ) : (
            <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center text-slate-500">
              <p className="font-medium text-slate-700">Nenhum aluno nesta turma</p>
              <p className="mt-1 text-sm">Adicione o primeiro aluno para começar.</p>
              {isManager && (
                <Button
                  className="mt-5 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleAddAluno}
                >
                  <UserPlus className="mr-2" size={18} />
                  Adicionar primeiro aluno
                </Button>
              )}
            </div>
          )}
        </section>
      </div>

      <AlertDialog
        open={!!alunoParaRemover}
        onOpenChange={open => {
          if (!open && !isRemoving) setAlunoParaRemover(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover aluno</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover{" "}
              <span className="font-semibold text-slate-900">
                {alunoParaRemover?.nome}
              </span>
              ? O histórico de presença também será removido e esta ação não poderá ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={isRemoving}
              onClick={event => {
                event.preventDefault();
                void confirmarRemocao();
              }}
            >
              {isRemoving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removendo...
                </>
              ) : (
                "Remover aluno"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showAddEditDialog && (
        <AddEditStudentDialog
          open={showAddEditDialog}
          onClose={() => setShowAddEditDialog(false)}
          onStudentAdded={handleStudentAdded}
          turmaId={turmaId || ""}
          student={selectedAluno}
          isEditing={isEditing}
        />
      )}
    </div>
  );
};

export default GerenciarAlunosPage;
